import { OptionSelection, ProjectInput, RequirementInstance, Standort, StandortTyp } from 'apiClient/Api';
import { RequirementItem, RequirementOptionGroup } from './calculatorApi';

/** Deutsche Anzeige-Labels für die Standort-Typen (statt der Enum-Strings). */
export const STANDORT_TYP_LABEL: Record<StandortTyp, string> = {
    [StandortTyp.StandAlone]: 'Stand-Alone',
    [StandortTyp.Child]: 'Versorgt',
    [StandortTyp.Connected]: 'Verbunden',
};

/** Verständliche Beschreibung je Standort-Typ (für Legende/Tooltip im UI). */
export const STANDORT_TYP_BESCHREIBUNG: Record<StandortTyp, string> = {
    [StandortTyp.StandAlone]:
        'Eigenständiger Standort mit eigener Zentraltechnik (Controller). Wird für sich allein betrieben und ist von keinem anderen Standort abhängig.',
    [StandortTyp.Child]:
        'Standort ohne eigenen Controller – wird von einem anderen Standort betrieben. Vor Ort stehen nur Komponenten wie Funk-Controller, UniKeys o. Ä., aber keine eigene Zentraltechnik.',
    [StandortTyp.Connected]:
        'Mehrere Standorte mit je eigenem Controller, die sich Ressourcen teilen. Der Verbund läuft über SIP und/oder Digitalfunk-Kopplung; bei Netzausfall arbeitet jeder Standort autark mit seinem lokalen Controller weiter.',
};

/** Der immer vorhandene Standard-Standort (Backend ergänzt ihn ebenfalls; in der UI nicht löschbar). */
export const DEFAULT_STANDORT_ID = 'A';
export const defaultStandort = (): Standort => ({ id: DEFAULT_STANDORT_ID, name: 'Standard', typ: StandortTyp.StandAlone });

/** Stellt sicher, dass der Standard-Standort enthalten ist (an erster Stelle). */
export const withDefaultStandort = (standorte: Standort[] | null | undefined): Standort[] => {
    const list = standorte ?? [];
    return list.some((s) => s.id === DEFAULT_STANDORT_ID) ? list : [defaultStandort(), ...list];
};

/**
 * UI-Zustand einer konfigurierbaren Anforderungs-Instanz (Variante). `opt` ist – wie in der
 * Vanilla-Vorlage – eine flache Map über ALLE (auch verschachtelten) Options-IDs der Instanz;
 * die Verschachtelung kommt beim Sammeln aus den Options-Definitionen.
 */
export interface Instance {
    iid: number;
    menge: number;
    area: string;
    setName: string;
    opt: Record<string, number>;
}

export type InstanceMap = Record<string, Instance[]>;

let nextIid = 1;

export const newInstance = (menge = 0, opt: Record<string, number> = {}): Instance => ({
    iid: nextIid++,
    menge,
    area: 'A',
    setName: '',
    opt: { ...opt },
});

// Sammelt die gewählten Optionen rekursiv aus den Optionsgruppen (jede Gruppe enthält Optionen,
// jede Option kann eigene Untergruppen haben).
const collectOptions = (
    groups: RequirementOptionGroup[] | undefined,
    optMap: Record<string, number>,
): OptionSelection[] => {
    if (!groups) return [];
    const out: OptionSelection[] = [];
    for (const g of groups) {
        for (const o of g.options) {
            const m = optMap[o.requirementId] || 0;
            if (m > 0) out.push({ requirementId: o.requirementId, menge: m, options: collectOptions(o.optionGroups, optMap) });
        }
    }
    return out;
};

/** Vorbelegung der `default="1"`-Optionen (rekursiv) beim Anlegen einer Instanz. */
export const defaultOptions = (groups: RequirementOptionGroup[] | undefined): Record<string, number> => {
    const out: Record<string, number> = {};
    const walk = (gs: RequirementOptionGroup[] | undefined) => {
        for (const g of gs || []) {
            for (const o of g.options) {
                if (o.isDefault) {
                    out[o.requirementId] = o.defaultAnzahl || 1;
                    walk(o.optionGroups);
                }
            }
        }
    };
    walk(groups);
    return out;
};

/** Baut aus dem UI-Zustand das an die API zu sendende Projekt (nur Instanzen mit Menge > 0). */
export const collectProject = (
    projektname: string,
    instances: InstanceMap,
    index: Map<string, RequirementItem>,
    standorte: Standort[],
): ProjectInput => {
    const instanzen: RequirementInstance[] = [];
    for (const reqId of Object.keys(instances)) {
        const item = index.get(reqId);
        for (const inst of instances[reqId]) {
            if (inst.menge > 0) {
                instanzen.push({
                    requirementId: reqId,
                    menge: inst.menge,
                    area: inst.area || DEFAULT_STANDORT_ID,
                    setName: inst.setName || null,
                    options: item ? collectOptions(item.optionGroups, inst.opt) : [],
                });
            }
        }
    }
    return { projektname, instanzen, standorte: withDefaultStandort(standorte) };
};

/** Stellt aus einem geladenen Projekt-JSON den UI-Zustand wieder her. */
export const projectToInstances = (project: ProjectInput): InstanceMap => {
    const map: InstanceMap = {};
    const flatten = (opts: OptionSelection[] | null | undefined, target: Record<string, number>) => {
        for (const o of opts || []) {
            if (o.requirementId) target[o.requirementId] = o.menge ?? 0;
            flatten(o.options, target);
        }
    };
    for (const inst of project.instanzen || []) {
        const reqId = inst.requirementId;
        if (!reqId) continue;
        if (!map[reqId]) map[reqId] = [];
        const created = newInstance(inst.menge ?? 0);
        created.area = inst.area || 'A';
        created.setName = inst.setName || '';
        flatten(inst.options, created.opt);
        map[reqId].push(created);
    }
    return map;
};

/** Set aller Anforderungs-IDs mit mindestens einer konfigurierten (Menge > 0) Instanz. */
export const configuredRequirementIds = (instances: InstanceMap): Set<string> => {
    const set = new Set<string>();
    for (const reqId of Object.keys(instances)) {
        if (instances[reqId].some((i) => i.menge > 0)) set.add(reqId);
    }
    return set;
};
